import React from "react";
import {
  Box,
  List,
  ListItem,
  Avatar,
  Typography,
  Badge,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListSubheader,
  Chip,
  Checkbox,
  ListItemText,
  Skeleton,
  LinearProgress,
  useTheme,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { getChannelIcon, getClientProfilePictureUrl, getInitials } from "../utils/utils";
import { getWhatsAppWindowState } from "../utils/whatsappWindowUtils";
import { useAuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { useConversationListLogic } from "../hooks/useConversationListLogic";
import { translateQuickFilterLabel } from "../i18n/chatListFilterLabels";
import StatusChipSelector, { normalizeStatusInput } from "./StatusChipSelector";
import AssigneeSelector from "./AssigneeSelector";
import { useRequests } from "../hooks/useRequests";
import { useUsers } from "../hooks/useUsers";
import { useAgents } from "../hooks/useAgents";
import { useChannels } from "../hooks/useChannels";
import { isWhatsAppQrChannelTipo } from "../constants/channelCatalog";

/** Estilo do pill por tipo de filtro (status usa cores do tema) */
function getFilterPillStyle(filter, theme) {
  const statusColors = {
    Pendente: theme.palette.status?.pending,
    "Em Atendimento": theme.palette.status?.inProgress,
    "Contato Futuro": theme.palette.status?.future,
    Concluída: theme.palette.status?.success,
    Perdida: theme.palette.status?.unsuccessful,
    Cancelada: theme.palette.status?.canceled,
  };
  const bg = statusColors[filter];
  if (!bg) return {};
  const useLightText = ["Concluída", "Perdida", "Cancelada"].includes(filter);
  return {
    backgroundColor: bg,
    color: useLightText ? theme.palette.common.white : theme.palette.text.primary,
    "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.8 },
  };
}

const ConversationList = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  refetchConversations,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  initialLoading,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
}) => {
  const { t: tChat } = useTranslation("chatList");
  const {
    filterAnchor,
    contextMenuAnchor,
    selectedForContextMenu,
    ownershipFilters,
    additionalFilters,
    isMeuDiaActive,
    setMeuDia,
    listRef,
    handleSearchChange,
    handleFilterClick,
    handleFilterClose,
    toggleFilter,
    handleOpenInPopup,
    handleContextMenu,
    handleContextMenuClose,
    handleItemClick,
    filteredConversations,
    bottomRef,
  } = useConversationListLogic({
    conversations,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    selectedConversation,
    onSelectConversation,
    refetchConversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const { userData } = useAuthContext();
  const theme = useTheme();

  const { updateMutation } = useRequests();
  // Precisa incluir ChatBots (ura=1) no seletor de responsável.
  const { data: usersResp } = useUsers(1, 150, "", "todos");
  const { data: agentsResp } = useAgents(1, 200, "");
  const { data: channelsData } = useChannels(1, 200, "");

  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const channels = channelsData?.channels ?? [];

  const getChannelTypeForConversation = (conv) => {
    const type = conv?.channelType ?? conv?.channel_type;
    if (type) return type;
    const channelId = conv?.channelId ?? conv?.id_origem ?? conv?.channel_id;
    if (channelId == null) return null;
    const ch = channels.find((c) => String(c.id) === String(channelId));
    return ch?.channelType ?? ch?.channel_type ?? null;
  };

  const getChannelTipoForConversation = (conv) => {
    const channelId = conv?.channelId ?? conv?.id_origem ?? conv?.channel_id;
    if (channelId == null) return null;
    const ch = channels.find((c) => String(c.id) === String(channelId));
    return ch?.tipo != null && ch?.tipo !== "" ? Number(ch.tipo) : null;
  };

  const isWhatsAppConversation = (conv) => {
    const type = getChannelTypeForConversation(conv);
    if (type && String(type).toLowerCase() === "whatsapp") return true;
    const channelName = (conv?.channel ?? conv?.channelLabel ?? "").toString().toLowerCase();
    return channelName.includes("whatsapp");
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "—";
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : date.toLocaleDateString();
  };

  const sanitizeText = (htmlString) => {
    if (!htmlString) return "—";
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    const text = (div.textContent || div.innerText || "").trim();
    return text || "—";
  };

  const getAgentId = (agent) => {
    if (!agent || typeof agent !== "object") return null;
    if (agent.id != null) return agent.id;
    if (agent.id_agente != null) return agent.id_agente;
    if (agent.agentId != null) return agent.agentId;

    const dynamicKey = Object.keys(agent).find((key) => {
      const lower = key.toLowerCase();
      return (
        lower.includes("id") &&
        agent[key] !== null &&
        agent[key] !== undefined &&
        (typeof agent[key] === "number" || !Number.isNaN(Number(agent[key])))
      );
    });

    return dynamicKey ? agent[dynamicKey] : null;
  };

  const buildAssigneeValue = (conversation) => {
    if (!conversation) {
      return { type: "queue", id: null, label: "Fila" };
    }

    const status = normalizeStatusInput(conversation?.status);
    const isClosed = status === 2 || status === 4 || status === 5;

    const assignedToValue =
      conversation?.assignedTo ?? conversation?.userName ?? null;

    const assignedIsQueueLabel =
      typeof assignedToValue === "string" &&
      ["fila", "queue"].includes(assignedToValue.toLowerCase());

    const hasUserByName =
      assignedToValue !== null &&
      assignedToValue !== undefined &&
      assignedToValue !== 0 &&
      assignedToValue !== "0" &&
      assignedToValue !== "" &&
      !assignedIsQueueLabel;

    const hasUserById =
      conversation?.userId !== null &&
      conversation?.userId !== undefined &&
      conversation?.userId !== 0 &&
      conversation?.userId !== "0";

    const agentName =
      conversation?.agentName ?? conversation?.agent_name ?? null;

    const rawAgentId =
      conversation?.agentId ??
      conversation?.id_agente ??
      conversation?.agenteId ??
      conversation?.idAgent ??
      null;

    const hasAgent = rawAgentId !== null && rawAgentId !== "" && rawAgentId !== 0 && rawAgentId !== "0";

    // Agente de IA tem prioridade sobre usuário
    if (hasAgent) {
      const id = rawAgentId;

      const fromList =
        agents.find((a) => String(getAgentId(a)) === String(id)) || null;

      const label =
        agentName ||
        fromList?.name ||
        fromList?.nome ||
        "Agente de IA";

      return { type: "agent", id, label };
    }

    if (hasUserByName || hasUserById) {
      const id = conversation?.userId ?? null;

      const fromList =
        id != null
          ? users.find((u) => String(u.id) === String(id)) || null
          : null;

      const isChatBot =
        fromList
          ? Number(fromList.ura) === 1
          : Number(conversation?.userUra) === 1;

      const baseLabel =
        assignedToValue ||
        fromList?.nome ||
        fromList?.name ||
        conversation?.userName ||
        "";

      const isCurrentUser =
        (id != null && String(id) === String(userData?.id)) ||
        (baseLabel && baseLabel === userData?.nome);

      const label = isCurrentUser
        ? "Minha Solicitação"
        : baseLabel || (isChatBot ? "ChatBot" : "Usuário");

      return { type: "user", id, label, isChatBot };
    }

    return {
      type: "queue",
      id: null,
      label: isClosed ? "Sem usuário" : "Fila",
    };
  };

  const [assigneeOverrides, setAssigneeOverrides] = React.useState({});

  const handleStatusChange = (conversation, newStatus) => {
    if (!conversation?.id) return;

    updateMutation.mutate({ id: conversation.id, status: newStatus });
  };

  const handleAssigneeChange = (conversation, next) => {
    if (!conversation?.id || !next) return;

    const payload = { id: conversation.id };

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;
    } else if (next.type === "agent") {
      // Resolve o ID do agente primeiro a partir do objeto bruto,
      // depois cai para o next.id e, por último, para o nome.
      let agentId = null;

      if (next.raw) {
        agentId = getAgentId(next.raw);
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agents.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId = getAgentId(found);
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;
    }

    setAssigneeOverrides((prev) => ({
      ...prev,
      [conversation.id]: next,
    }));

    updateMutation.mutate(payload);
  };

  return (
    <Box
      flexGrow={1}
      overflow="auto"
      className="sidebar-scroll"
      ref={listRef}
    >
      {/* Busca + atalho Meu dia + menu Filtros */}
      <Box
        className="tour-chat-list-filters"
        display="flex"
        alignItems="center"
        p={1}
        gap={0.75}
        flexWrap="nowrap"
      >
        <TextField
          size="small"
          placeholder={tChat("searchPlaceholder")}
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            "& .MuiOutlinedInput-root": { borderRadius: 1 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title={tChat("meuDiaTooltip")}>
          <Chip
            label={tChat("meuDia")}
            size="small"
            onClick={setMeuDia}
            variant={isMeuDiaActive ? "filled" : "outlined"}
            color={isMeuDiaActive ? "primary" : "default"}
            sx={{
              fontWeight: 600,
              flex: "0 1 132px",
              minWidth: 0,
              maxWidth: "40%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              },
            }}
          />
        </Tooltip>
        <Tooltip title={tChat("moreFiltersTooltip")}>
          <IconButton
            size="small"
            onClick={handleFilterClick}
            aria-label={tChat("filtersMenuAria")}
            sx={{ flexShrink: 0 }}
          >
            <FilterListIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { minWidth: 240, maxHeight: 420 } }}
        >
          <ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2 }}>
            {tChat("sectionWho")}
          </ListSubheader>
          {ownershipFilters.map((filter) => (
            <MenuItem key={filter} onClick={() => toggleFilter(filter)} dense>
              <Checkbox size="small" checked={filters.includes(filter)} sx={{ mr: 1 }} />
              <ListItemText primary={translateQuickFilterLabel(filter, tChat)} />
            </MenuItem>
          ))}
          <ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2 }}>
            {tChat("sectionStatus")}
          </ListSubheader>
          {additionalFilters
            .filter((f) => f !== "Não Lidas")
            .map((filter) => (
              <MenuItem key={filter} onClick={() => toggleFilter(filter)} dense>
                <Checkbox size="small" checked={filters.includes(filter)} sx={{ mr: 1 }} />
                <ListItemText primary={translateQuickFilterLabel(filter, tChat)} />
              </MenuItem>
            ))}
          <ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2 }}>
            {tChat("sectionOther")}
          </ListSubheader>
          <MenuItem onClick={() => toggleFilter("Não Lidas")} dense>
            <Checkbox size="small" checked={filters.includes("Não Lidas")} sx={{ mr: 1 }} />
            <ListItemText primary={translateQuickFilterLabel("Não Lidas", tChat)} />
          </MenuItem>
        </Menu>
      </Box>

      {/* Pills dos filtros ativos — cores por status */}
      {filters?.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={0.5} px={1} pb={0.75}>
          {filters.map((filter) => {
            const statusStyle = getFilterPillStyle(filter, theme);
            return (
              <Chip
                key={filter}
                label={translateQuickFilterLabel(filter, tChat)}
                size="small"
                onDelete={() => toggleFilter(filter)}
                sx={{
                  fontSize: "0.75rem",
                  height: 24,
                  ...statusStyle,
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Loading ao trocar filtros/busca (sempre visível abaixo dos pills) */}
      {isFetching && !initialLoading && (
        <Box
          sx={{
            px: 1.5,
            width: "100%",
            height: 0,
            overflow: "visible",
            position: "relative",
          }}
        >
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 4,
              borderRadius: 3,
            }}
          />
        </Box>
      )}

      {!initialLoading && !isFetching && filteredConversations.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 5,
            px: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Nenhuma solicitação no momento
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            Novas solicitações aparecerão aqui quando forem criadas.
          </Typography>
        </Box>
      ) : (
        <>
      <List>
        {filteredConversations.map((conversation) => (
          <ListItem
            key={conversation.id}
            onClick={(event) => handleItemClick(event, conversation)}
            onContextMenu={(event) => handleContextMenu(event, conversation)}
            sx={{
              cursor: "pointer",
              backgroundColor:
                selectedConversation?.id === conversation.id
                  ? theme.palette.action.selected
                  : "transparent",
              "&:hover": { backgroundColor: theme.palette.action.hover },
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "flex-start",
              padding: "12px 16px",
              gap: 1.5,
              py: 1.5,
              minHeight: 72,
            }}
          >
            <Badge
              color="error"
              variant="dot"
              overlap="circular"
              invisible={
                !conversation?.unreadClient ||
                Number(conversation?.unreadClient) === 0
              }
            >
              <Avatar
                src={getClientProfilePictureUrl(conversation?.profile_picture) || undefined}
                sx={{ width: 40, height: 40, backgroundColor: theme.palette.primary.light }}
              >
                {getInitials(conversation?.name)}
              </Avatar>
            </Badge>
            <Box flexGrow={1} minWidth={0} overflow="hidden" display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: "1 1 auto",
                    minWidth: 0,
                      fontWeight:
                        (conversation?.unreadClient ?? 0) > 0 ? 700 : 500,
                    fontSize: "0.9375rem",
                  }}
                >
                  {conversation.name || "—"}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.75} flexShrink={0}>
                  {isWhatsAppConversation(conversation) &&
                    !isWhatsAppQrChannelTipo(getChannelTipoForConversation(conversation)) &&
                    (() => {
                      const lastClientAtRaw =
                        conversation.lastClientMessageAt ??
                        conversation.last_client_message_at ??
                        null;
                      const lastClientAt =
                        lastClientAtRaw != null && lastClientAtRaw !== ""
                          ? lastClientAtRaw
                          : conversation.timestamp;
                      const windowState = getWhatsAppWindowState(lastClientAt);
                      if (windowState.status === "aboutToExpire" || windowState.status === "expired") {
                        const iconColor =
                          windowState.status === "aboutToExpire"
                            ? theme.palette.warning.main
                            : theme.palette.error.main;
                        return (
                          <Tooltip title={windowState.tooltip} placement="left" arrow>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 24,
                                minHeight: 24,
                                color: iconColor,
                              }}
                              aria-label="Janela de 24h WhatsApp"
                            >
                              <ScheduleIcon sx={{ fontSize: 20 }} />
                            </Box>
                          </Tooltip>
                        );
                      }
                      return null;
                    })()}
                  <Typography variant="caption" color={theme.palette.text.secondary} sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                    {formatTimestamp(conversation.timestamp)}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                color={theme.palette.text.secondary}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "0.8125rem",
                  lineHeight: 1.35,
                }}
              >
                {sanitizeText(conversation.lastMessage)}
              </Typography>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mt={0.5}>
                {getChannelIcon(conversation.channel ?? null, getChannelTypeForConversation(conversation))}
                <Tooltip title={`Protocolo: ${conversation?.id || ""}`}>
                  <Chip
                    label={`#${conversation?.id || ""}`}
                    size="small"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: "22px",
                      maxWidth: "100px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Tooltip>

                <AssigneeSelector
                  value={assigneeOverrides[conversation.id] ?? buildAssigneeValue(conversation)}
                  onChange={(next) => handleAssigneeChange(conversation, next)}
                  users={users}
                  agents={agents}
                  size="small"
                />

                <StatusChipSelector
                  currentStatus={conversation.status}
                  onStatusChange={(newStatus) =>
                    handleStatusChange(conversation, newStatus)
                  }
                  size="small"
                />
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
      <div ref={bottomRef} style={{ height: 1 }} />

      {isFetchingNextPage && (
        <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
        </>
      )}

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuAnchor ? { top: contextMenuAnchor.mouseY, left: contextMenuAnchor.mouseX } : undefined
        }
        open={Boolean(contextMenuAnchor)}
        onClose={handleContextMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleOpenInPopup(selectedForContextMenu);
            handleContextMenuClose();
          }}
        >
          Abrir em nova janela
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ConversationList;
